/**
 * Lettaga Reserve Name 폼 값 → CRS `rocNameReservation/saveDraft` payload 매퍼.
 *
 * 근거: LETTAGA.SCRAPPING_NEW/MY/docs/captures/2026-09-28_name-reservation/
 *        NOTES.md + network/01_POST_rocNameReservation_saveDraft.json (실제 Save 캡처)
 *      + roc-registration 번들(219.*.js) — 코드 선택 규칙을 직접 읽어 확인했다.
 *
 * 확정 사실:
 *   - saveDraft 하나가 **생성 겸 갱신**이다(설립 save 와 같다).
 *     생성은 submissionId / nameReservationId / rocNameReservationId 를 빈 문자열로 보내고
 *     셋 다 응답으로 받는다. 갱신은 받은 값을 되돌려 보낸다.
 *   - Name Search 화면(카테고리/회사종류/서브타입/접미사/신청종류)의 선택값은
 *     **이미 CRS 코드 ID** 다(nameSearchMeta.json 이 CRS 코드를 그대로 담고 있다).
 *     그래서 이 블록은 코드표 변환이 필요 없다.
 *   - Clarification 은 화면에 안 쓴 문항까지 **7건 모두** 보낸다(빈 답으로).
 *   - 6번 Business Code 의 답은 `answer` 가 아니라 `businessCodeAnswer[]` 다.
 *
 * ⚠️ draft 까지만이다. CRS 의 제출(saveApplication/saveSubmission)은 호출하지 않는다.
 */

import salutationsCrs from '../../shared/data/idpro/salutations-crs.json';
import { toCrsEpochMs, resolveCode, type LookupRow } from
  '../../direct-incorporation/composables/useCrsDraftPayload';
import { toCrsIdKey, requiresIdExpiryDate, requiresSecondaryId } from '../../shared/crsIdentityRules';

// ── 상수(실측 확정) ───────────────────────────────────────────────

/**
 * 이름 예약 유형. 번들의 `NR_TYPE_NEW_INCORP`("NEW_INCORP") 에 해당하는 코드다.
 * 이 프리셋은 항상 신규 설립용 예약이라 값이 변하지 않는다.
 */
export const NR_TYPE_NEW_INCORPORATION = 11028;

/**
 * 이름 조회 결과에서 고른 진행 방향. 번들의 `NR_Name_Reservation`("NR").
 * (DI 로 바로 가는 경우와 갈리는 값 — 우리는 항상 이름 예약이다.)
 */
export const PROPOSED_OPTION_NAME_RESERVATION = 11133;

/** 선언문 종류. `rocCiuCommon_lookup.declarationCode` 의 "COMMON DECLARATION". */
export const DECLARATION_CONTENT_TYPE_CODE_ID = 43196;
export const DECLARATION_TEMPLATE_NAME = 'CommonDeclarationTemplate';

/** Promoter Type — 모달 라디오 실측값. */
export const PROMOTER_TYPE = { INDIVIDUAL: 34126, BODY_CORPORATE: 34127 } as const;

/** Registered with SSM — 모달 라디오 실측값. */
export const SSM_REGISTERED = { YES: 11090, NO: 11091 } as const;

/** Name Search 의 Category 코드(nameSearchMeta 와 같은 값). */
export const CATEGORY = { LOCAL: '34125', FOREIGN: '34124' } as const;

/** Type of Company 코드. */
export const COMPANY_TYPE = {
  LIMITED_BY_SHARES: '34101',
  UNLIMITED: '34102',
  LIMITED_BY_GUARANTEE: '34103',
} as const;

/**
 * formTypeCode — 번들의 결정 함수를 그대로 옮긴 것이다.
 *
 *   function(categoryList, companyTypeList, category, companyType, isGazette) {
 *     if (category === FOREIGN)                       return CNRNRROCFGN;
 *     switch (companyType) {
 *       case UNLIMITED: case LIMITED_BY_SHARES:       return CNRNRROCLCL;
 *       case LIMITED_BY_GUARANTEE:                    return isGazette ? …GZT : …NOGZT;
 *     }
 *   }
 *
 * CLBG = Company Limited By Guarantee, GZT = 관보(gazette) 단어가 걸린 경우.
 * gazette 여부는 이름 조회 결과의 `gazetteWordList` 에서 온다(아래 nameCheckFlags).
 */
export const FORM_TYPE_CODE = {
  LOCAL: 'CNRNRROCLCL',
  FOREIGN: 'CNRNRROCFGN',
  CLBG_WITHOUT_GAZETTE: 'CNRNRROCCLBGNOGZT',
  CLBG_WITH_GAZETTE: 'CNRNRROCCLBGGZT',
} as const;

/**
 * Clarification 문항 코드. 화면 번호 → CRS questionId.
 * 실제 Save 캡처에서 이 7건이 이 순서로 나갔다. CRS 코드표에는 Q1~Q8 이 있지만
 * 화면에 뜨는 것은 7개이고, 7번 "Other Comments" 가 11052 다(11053 은 화면에 없다).
 */
export const CLARIFICATION_QUESTION_ID = {
  clarification01: 11059,
  clarification02: 11058,
  clarification03: 11057,
  clarification04: 11056,
  clarification05: 11055,
  businessCodes: 11054,
  clarification07: 11052,
} as const;

/** 보내는 순서(=화면 순서). CRS 도 이 순서로 보낸다. */
const CLARIFICATION_ORDER: (keyof typeof CLARIFICATION_QUESTION_ID)[] = [
  'clarification01', 'clarification02', 'clarification03', 'clarification04',
  'clarification05', 'businessCodes', 'clarification07',
];

/** scaffold 로 받아야 하는 코드표. 전체(약 985KB)를 받지 않도록 이것만 요청한다. */
export const REQUIRED_LOOKUP_TABLES = [
  'msicCode',
  'RocIncIdentityType',
  'IdTypeSecondaryCode',
  'salutation',
  'registeredEntity',
  'RegistrationNoFormatCode',
  'RocEntityType',
  'EntitySubTypeCode',
] as const;

// ── 헬퍼 ──────────────────────────────────────────────────────────

const str = (v: any): string => String(v ?? '').trim();

/**
 * CRS 경칭표(`salutationVO`) 31종을 그대로 담은 정적 표.
 * 코드표가 안 내려온 경우의 마지막 폴백이다 — 화면 선택지도 이 표에서 나온다.
 */
function crsSalutationCode(v: any): number | null {
  const want = String(v ?? '').trim().toUpperCase();
  if (!want) return null;
  const hit = (salutationsCrs as Array<{ value: string; crsCode: number }>)
    .find((r) => r.value.toUpperCase() === want);
  return hit ? hit.crsCode : null;
}
const upper = (v: any): string => str(v).toUpperCase();
const norm = (v: any): string => upper(v).replace(/\s+/g, ' ');
const numOrNull = (v: any): number | null => {
  const n = Number(str(v));
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Lettaga 신분증 값 → CRS 1차 신분증 코드 ID. */
function idTypeCode(lk: Lookup, ns: NsLookup | undefined, idType: any): number | null {
  const key = toCrsIdKey(idType);
  if (!key) return null;
  return nsId(ns, 'identificationTypeVO', key) ?? resolveCode(lk, 'RocIncIdentityType', key);
}

/**
 * 2차 신분증 코드 ID. CRS 2차 표(`IdTypeSecondaryCode`)의 키는 한 글자다(P/K/N/Y).
 * 코드표로 먼저 풀고, 표가 안 왔을 때만 설립에서 확정한 상수로 떨어진다.
 */
const SECONDARY_ID_FALLBACK: Record<string, number> = {
  P: 31006, K: 31007, POLICE: 41165, N: 34100,
};
function secondaryIdTypeCode(lk: Lookup, ns: NsLookup | undefined, v: any): number | null {
  const k = norm(v).replace(/[_-]/g, ' ');
  const shortKey = !k || k === 'PASSPORT' || k === 'P' ? 'P'
    : (k === 'OLD IC' || k === 'OLDIC' || k === 'K') ? 'K'
      : (k === 'POLICE ID' || k === 'POLICEID') ? 'POLICE'
        : (k === 'NONE' || k === 'N') ? 'N' : 'P';
  const nsKey = shortKey === 'POLICE' ? 'Y' : shortKey;
  const viaNs = nsId(ns, 'secondaryIdentificationTypeVO', nsKey);
  if (viaNs) return viaNs;
  if (shortKey !== 'POLICE') {
    const viaLookup = resolveCode(lk, 'IdTypeSecondaryCode', shortKey);
    if (viaLookup) return viaLookup;
  }
  return SECONDARY_ID_FALLBACK[shortKey] ?? SECONDARY_ID_FALLBACK.P;
}

/** 법인 등록 유형 — CRS `registeredEntity` 표(C=ROC / B=ROB / P=LLP). */
function registeredEntityCode(lk: Lookup, ns: NsLookup | undefined, v: any): number | null {
  const k = norm(v).replace(/[_-]/g, ' ');
  const key = /(^|\s)(ROC|REGISTRATION OF COMPAN)/.test(k) ? 'C'
    : /(LLP|LIMITED LIABILITY PARTNERSHIP)/.test(k) ? 'P'
      : null;
  if (!key) return null;
  return nsId(ns, 'promoterBodyCorpRegTypeVO', key) ?? resolveCode(lk, 'registeredEntity', key);
}

/** 등록번호 표기(구/신) — CRS `RegistrationNoFormatCode` 표(O=구 / N=신). */
function registrationNoFormatCode(lk: Lookup, ns: NsLookup | undefined, v: any): number | null {
  const k = norm(v);
  if (!k) return null;
  const key = k.startsWith('O') ? 'O' : k.startsWith('N') ? 'N' : null;
  if (!key) return null;
  // ns 표는 키가 OLD/NEW, 설립 표는 O/N 이다.
  return nsId(ns, 'promoterBodyCorpRegNoFormatVO', key === 'O' ? 'OLD' : 'NEW')
    ?? resolveCode(lk, 'RegistrationNoFormatCode', key);
}

/**
 * "45101 - Wholesale and Retail of New Motor Vehicles" → CRS businessCodeId.
 *
 * 화면 앞쪽의 MSIC 코드만 떼어 `msicCode` 표의 codeValue/key 와 직접 대조한다.
 * ★ 여기서 resolveCode 를 쓰면 안 된다 — 그 함수는 "숫자로만 된 값은 이미 CRS 코드 ID"
 *   로 보고 그대로 돌려준다. MSIC 는 "45101" 처럼 숫자라, 표를 한 번도 안 보고
 *   **MSIC 번호를 CRS 내부 id 자리에 그대로** 싣게 된다(45101 ≠ 32682).
 *   설립(DI) 도 같은 이유로 행을 직접 찾는다.
 */
function businessCodeId(lk: Lookup, ns: NsLookup | undefined, entry: any): number | null {
  const raw = str(entry);
  if (!raw) return null;
  const code = (raw.split('-')[0] || '').trim() || raw;
  const find = (rows?: LookupRow[]) => (rows || []).find(
    (r) => str((r as any).codeValue) === code || str(r.key) === code,
  );
  const hit = find(ns?.businessCodeVO) || find(lk?.msicCode);
  return hit ? hit.id : null;
}

// ── 타입 ──────────────────────────────────────────────────────────

type Lookup = Record<string, LookupRow[]> | undefined;

/**
 * 이름 예약 화면 전용 코드표(`nameSearch/nameSearchUiElements`).
 *
 * CRS 의 이름 예약 Promoter 모달은 설립 코드표가 아니라 **이 표**의 값을 쓴다
 * (번들의 `nameSearchLookUp.promoterTypeVO / identificationTypeVO / … ` ).
 * 2026-09-29 실측에서 두 표의 값이 지금은 모두 같았지만, 정본은 이쪽이므로 먼저 본다.
 * 표가 안 왔을 때만 설립 표(또는 상수)로 떨어진다.
 */
export interface NsLookup {
  promoterTypeVO?: LookupRow[];
  identificationTypeVO?: LookupRow[];
  secondaryIdentificationTypeVO?: LookupRow[];
  salutationVO?: LookupRow[];
  ssmRegisterVO?: LookupRow[];
  promoterBodyCorpRegTypeVO?: LookupRow[];
  promoterBodyCorpRegNoFormatVO?: LookupRow[];
  promoterBodyCorpEntityTypeVO?: LookupRow[];
  promoterBodyCorpEntitySubTypeVO?: LookupRow[];
  businessCodeVO?: LookupRow[];
  nrTypeVO?: LookupRow[];
  proposedOption?: LookupRow[];
  contentTypeCodeVO?: LookupRow[];
  [k: string]: any;
}

/** ns 표에서 심볼릭 키로 id 찾기. 표가 없거나 못 찾으면 null. */
function nsId(ns: NsLookup | undefined, table: keyof NsLookup, key: any): number | null {
  const rows = ns?.[table];
  if (!Array.isArray(rows)) return null;
  const want = norm(key);
  if (!want) return null;
  const hit = rows.find((r: any) => norm(r?.key) === want)
    || rows.find((r: any) => norm(r?.description) === want);
  return hit && Number.isFinite(Number(hit.id)) ? Number(hit.id) : null;
}

export interface NrPromoterInput {
  promoterType?: string;
  promoterName?: string;
  identificationType?: string;
  identificationNumber?: string;
  idExpiryDate?: string;
  secondaryIdType?: string;
  secondaryIdNumber?: string;
  secondaryIdExpiryDate?: string;
  salutation?: string;
  dateOfBirth?: string;
  email?: string;
  isSsmRegistered?: string;
  compRegType?: string;
  compRegNoFormat?: string;
  compRegNo?: string;
  memberCategory?: string;
  memberSubCategory?: string;
  entityNo?: string;
}

export interface BuildNrPayloadInput {
  /** ReserveNameForm.getFormData() 결과 */
  formData: Record<string, any>;
  /** scaffold.lookup (설립 코드표) */
  lookup?: Lookup;
  /** scaffold.ns_lookup (이름 예약 전용 코드표 — 있으면 이쪽이 우선) */
  nsLookup?: NsLookup | null;
  /** scaffold.declaration — {contentId, msgTemplateId, content, templateName} */
  declaration?: Record<string, any> | null;
  /** scaffold.lodger — declarationInfo.lodgerInfo 에 **그대로** 들어간다 */
  lodger?: Record<string, any> | null;
  /**
   * 이름 조회 결과의 충돌 목록(`nameCheck.conflicts`).
   * CRS 는 이 셋을 payload 최상위 플래그로 되돌려 보낸다(번들 확인):
   *   proposeNameGazette ← gazetteWordList, proposeNameControl ← controlWordList,
   *   proposeNameState   ← stateNameMalaysiaList
   */
  conflicts?: Record<string, any[]> | null;
  /** 갱신이면 앞서 받은 id 3종. 신규면 생략(빈 문자열로 나간다) */
  submissionId?: string;
  nameReservationId?: string;
  rocNameReservationId?: string;
  /** 갱신 대상 draft 의 현재 내용(`draftDetail` 응답) — 하위 항목 id 유지용 */
  existingDetail?: Record<string, any> | null;
}

export interface BuildNrPayloadResult {
  payload: Record<string, any>;
  /** 보내긴 하지만 사용자에게 알려야 하는 것 */
  warnings: string[];
  /** 보내면 안 되는 것 — 있으면 호출부가 전송을 막는다 */
  blockingIssues: string[];
}

// ── 조립 ──────────────────────────────────────────────────────────

/** 이름 조회 결과 → payload 최상위 플래그 3종. */
export function nameCheckFlags(conflicts?: Record<string, any[]> | null) {
  const has = (k: string) => Array.isArray(conflicts?.[k]) && (conflicts as any)[k].length > 0;
  return {
    proposeNameGazette: has('gazetteWordList'),
    proposeNameControl: has('controlWordList'),
    proposeNameState: has('stateNameMalaysiaList'),
  };
}

/** category / companyType / gazette → formTypeCode. */
export function resolveFormTypeCode(
  category: any, companyType: any, isGazette: boolean,
): string {
  if (str(category) === CATEGORY.FOREIGN) return FORM_TYPE_CODE.FOREIGN;
  if (str(companyType) === COMPANY_TYPE.LIMITED_BY_GUARANTEE) {
    return isGazette ? FORM_TYPE_CODE.CLBG_WITH_GAZETTE : FORM_TYPE_CODE.CLBG_WITHOUT_GAZETTE;
  }
  // LIMITED BY SHARES / UNLIMITED — 번들도 이 둘을 한 값으로 묶는다.
  return FORM_TYPE_CODE.LOCAL;
}

/** clarification 텍스트 뽑기. 03~05 는 `{text, fileName…}` 객체다. */
function clarificationText(formData: Record<string, any>, field: string): string {
  const v = formData[field];
  if (v && typeof v === 'object') return str((v as any).text);
  return str(v);
}

/** 기존 draft 에서 같은 문항의 id 를 찾는다(갱신일 때 행 중복 방지). */
function existingClarificationId(detail: any, questionId: number): string {
  const rows = detail?.clarificationsInfo?.clarificationDetailList;
  if (!Array.isArray(rows)) return '';
  const hit = rows.find((r: any) => Number(r?.questionId) === questionId);
  return str(hit?.rocnrclarificationid);
}

/**
 * 기존 draft 에서 같은 프로모터의 id 를 찾는다. 개인=신분증번호, 법인=등록/법인번호+이름.
 *
 * ⚠️ 한 번 쓴 id 는 다시 주지 않는다(`used`). 같은 신분증번호가 두 번 들어오면 두 행이
 *   **같은 CRS 행 id** 를 들고 나가 한 명이 덮어써진다(설립도 usedPartyIds 로 막는다).
 */
function existingPromoterId(
  detail: any, listKey: string, used: Set<string>, match: (r: any) => boolean,
): string {
  const rows = detail?.promoterInfo?.[listKey];
  if (!Array.isArray(rows)) return '';
  const hit = rows.find((r: any) => {
    const id = str(r?.rocNrPromoterId);
    return id && !used.has(id) && match(r);
  });
  if (!hit) return '';
  const id = str(hit.rocNrPromoterId);
  used.add(id);
  return id;
}

export function buildNrDraftPayload(input: BuildNrPayloadInput): BuildNrPayloadResult {
  const f = input.formData || {};
  const lk = input.lookup;
  const ns = input.nsLookup || undefined;
  const warnings: string[] = [];
  const blockingIssues: string[] = [];
  const detail = input.existingDetail || null;

  const flags = nameCheckFlags(input.conflicts);
  const proposedName = upper(f.entityName);

  // ── applicationInfo ─────────────────────────────────────────────
  const applicationInfo = {
    nrTypeId: nsId(ns, 'nrTypeVO', 'NEW_INCORP') ?? NR_TYPE_NEW_INCORPORATION,
    purposeId: numOrNull(f.purpose),
    applicationTypeId: numOrNull(f.typeOfApplication),
    proposedCompanyName: proposedName,
    localForeignId: numOrNull(f.category),
    companyTypeId: numOrNull(f.companyType),
    subTypeId: numOrNull(f.subType),
    companySuffix: numOrNull(f.suffix),
    supportingDocInfo: { supportingDocList: [] as any[] },
    proposedOption: nsId(ns, 'proposedOption', 'NR') ?? PROPOSED_OPTION_NAME_RESERVATION,
  };

  // ── clarificationsInfo — 7건 전부, 화면 순서대로 ─────────────────
  const codes = (Array.isArray(f.businessCodes) ? f.businessCodes : []).filter(Boolean);
  const businessCodeAnswer: any[] = [];
  codes.forEach((entry: any, i: number) => {
    const id = businessCodeId(lk, ns, entry);
    if (id) {
      businessCodeAnswer.push({
        businessCodeId: id, rocNrClarificationBizCodeId: '', sequence: i + 1,
      });
    } else {
      // 조용히 빠지면 CRS 화면에서 6번이 비어 있는 채로 저장된다 — 막는다.
      blockingIssues.push(`Business code not found in CRS: ${str(entry)}`);
    }
  });

  const clarificationDetailList = CLARIFICATION_ORDER.map((key) => {
    const questionId = CLARIFICATION_QUESTION_ID[key];
    const isBizCode = key === 'businessCodes';
    return {
      rocnrclarificationid: existingClarificationId(detail, questionId),
      questionId,
      answer: isBizCode ? '' : clarificationText(f, key),
      businessCodeAnswer: isBizCode ? businessCodeAnswer : [],
      businessNatureAnswer: [] as any[],
      supportingDocInfo: { supportingDocList: [] as any[] },
    };
  });

  // 첨부는 아직 CRS 업로드 경로를 확보하지 못했다 — 조용히 버리지 않고 알린다.
  const attachedFields = ['clarification03', 'clarification04', 'clarification05']
    .filter((k) => str((f as any)[k]?.fileName));
  if (attachedFields.length) {
    warnings.push(
      'Clarification attachments are not sent to CRS yet — please attach them in CRS before submitting.',
    );
  }

  // ── promoterInfo ────────────────────────────────────────────────
  const individualPromoterVOList: any[] = [];
  const bodyCorporatePromoterVOList: any[] = [];
  // 기존 draft 의 프로모터 행 id 를 두 번 쓰지 않도록 소비 기록을 남긴다.
  const usedPromoterIds = new Set<string>();

  (Array.isArray(f.promoters) ? f.promoters : []).forEach((p: NrPromoterInput, i: number) => {
    const sequence = i + 1;
    if (str(p.promoterType) === 'BODY_CORPORATE') {
      const isSsm = str(p.isSsmRegistered) === 'true';
      const regNo = upper(isSsm ? p.compRegNo : p.entityNo);
      const entityName = upper(p.promoterName);
      bodyCorporatePromoterVOList.push({
        rocNrPromoterId: existingPromoterId(detail, 'bodyCorporatePromoterVOList', usedPromoterIds,
          (r: any) => upper(r?.registrationEntityNo) === regNo
            && upper(r?.companyEntityName) === entityName),
        sequence,
        promoterTypeId: nsId(ns, 'promoterTypeVO', 'BODY_CORPORATE') ?? PROMOTER_TYPE.BODY_CORPORATE,
        ssmRegistered: isSsm
          ? (nsId(ns, 'ssmRegisterVO', 'Y') ?? SSM_REGISTERED.YES)
          : (nsId(ns, 'ssmRegisterVO', 'N') ?? SSM_REGISTERED.NO),
        // SSM 등록 분기
        registrationType: isSsm ? registeredEntityCode(lk, ns, p.compRegType) : null,
        registrationEntityNoFormat: isSsm ? registrationNoFormatCode(lk, ns, p.compRegNoFormat) : null,
        registrationEntityNo: regNo || null,
        companyEntityName: entityName,
        // SSM 미등록 분기
        entityType: isSsm ? null
          : (nsId(ns, 'promoterBodyCorpEntityTypeVO', p.memberCategory)
            ?? resolveCode(lk, 'RocEntityType', p.memberCategory)),
        entitySubType: isSsm ? null
          : (nsId(ns, 'promoterBodyCorpEntitySubTypeVO', p.memberSubCategory)
            ?? resolveCode(lk, 'EntitySubTypeCode', p.memberSubCategory)),
        email: str(p.email) || null,
      });
      if (isSsm && !registeredEntityCode(lk, ns, p.compRegType)) {
        blockingIssues.push(`Promoter ${sequence}: type of registration not found in CRS.`);
      }
      return;
    }

    const idType = p.identificationType;
    const useExpiry = requiresIdExpiryDate(idType);
    const useSecondary = requiresSecondaryId(idType);
    const idNo = upper(p.identificationNumber);
    const crsIdType = idTypeCode(lk, ns, idType);
    if (!crsIdType) {
      blockingIssues.push(`Promoter ${sequence}: identification type cannot be filed to CRS.`);
    }
    /**
     * CRS 의 경칭표는 **honorific 31종**(DATIN / DATO' / TAN SRI …)뿐이다.
     * 우리 화면 목록에는 MR·MRS·MS·MADAM·DR·PROF·IR 도 있는데 CRS 에는 없어서,
     * 그대로 두면 **아무 말 없이 사라진다**(실측: 'DR' 을 보냈더니 CRS 에 salutation 이 없음).
     * 조용히 버리지 않고 알린다.
     */
    const salutationId = nsId(ns, 'salutationVO', p.salutation)
      ?? resolveCode(lk, 'salutation', p.salutation)
      ?? crsSalutationCode(p.salutation);
    if (str(p.salutation) && salutationId == null) {
      warnings.push(
        `Salutation "${str(p.salutation)}" is not in the CRS list, so it was not sent`
        + ` (${str(p.promoterName) || 'promoter'}). CRS only accepts honorific titles such as DATO' or TAN SRI.`,
      );
    }
    individualPromoterVOList.push({
      rocNrPromoterId: existingPromoterId(detail, 'individualPromoterVOList', usedPromoterIds,
        (r: any) => upper(r?.idNo) === idNo && Number(r?.idTypeId) === Number(crsIdType)),
      sequence,
      individualName: upper(p.promoterName),
      promoterTypeId: nsId(ns, 'promoterTypeVO', 'INDIVIDUAL') ?? PROMOTER_TYPE.INDIVIDUAL,
      idTypeId: crsIdType,
      idNo,
      idExpDate: useExpiry ? toCrsEpochMs(p.idExpiryDate) : null,
      secondaryIdTypeId: useSecondary ? secondaryIdTypeCode(lk, ns, p.secondaryIdType) : null,
      secondaryIdNo: useSecondary ? upper(p.secondaryIdNumber) || null : null,
      secondaryIdExpDate: useSecondary ? toCrsEpochMs(p.secondaryIdExpiryDate) : null,
      individualDob: toCrsEpochMs(p.dateOfBirth),
      email: str(p.email) || null,
      salutation: salutationId,
    });
  });

  // ── declarationInfo ─────────────────────────────────────────────
  //   lodgerInfo 는 CRS 가 내려 준 블록을 **그대로** 되돌려 보낸다(설립과 같은 방식).
  const decl = input.declaration || {};
  const declarationInfo = {
    lodgerInfo: input.lodger || {},
    declarationList: [] as any[],
    declarationTemplate: str((decl as any).content),
    templateName: str((decl as any).templateName) || DECLARATION_TEMPLATE_NAME,
    contentTypeCodeId: nsId(ns, 'contentTypeCodeVO', 'Declaration') ?? DECLARATION_CONTENT_TYPE_CODE_ID,
    contentId: str((decl as any).contentId),
  };
  if (!declarationInfo.declarationTemplate) {
    warnings.push('Declaration template was not returned by CRS — the draft may show an empty declaration.');
  }
  if (!input.lodger || !Object.keys(input.lodger).length) {
    blockingIssues.push('Lodger information could not be read from CRS.');
  }

  const payload = {
    submissionId: str(input.submissionId),
    nameReservationId: str(input.nameReservationId),
    rocNameReservationId: str(input.rocNameReservationId),
    applicationInfo,
    clarificationsInfo: { clarificationDetailList },
    promoterInfo: { individualPromoterVOList, bodyCorporatePromoterVOList },
    supportingDocInfo: { supportingDocList: [] as any[], declarationList: [] as any[] },
    declarationInfo,
    proposedName,
    ...flags,
    formTypeCode: resolveFormTypeCode(f.category, f.companyType, flags.proposeNameGazette),
  };

  return { payload, warnings, blockingIssues };
}
